import React, { ReactElement } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router';
import {
  AnyValue,
  Filter,
  MenuItemProps,
  SortingRule,
  Table,
  TableContainer,
  TableProps,
} from '@odin-labs/components';
import { to } from 'acl';
import { ApolloError } from '@apollo/client';
import {
  GetJobsiteWorkersOrderByField,
  JobsiteWorkerOrientationStatus,
  useGetOnboardingJobsiteWorkersQuery,
} from 'apollo/generated/client-operations';
import { AuthContext } from 'auth';
import { UserPlusIcon } from 'components/icons';
import { Container } from 'components/container';
import { NewHeader } from 'components/header/NewHeader';
import { LoadingError } from 'components/loadingError';
import {
  AddWorkerModal,
  EditSelfOnboardingWorkerWarningModal,
} from 'containers/worker/modals';
import { useAvailableJobsites } from 'graphql/client/useAvailableJobsites';
import {
  ensureNonEmptyItems,
  isStringEnumValue,
  getCurrentISOFormattedDate,
  isQueryLoading,
  useBoolean,
  useQueryOrderBy,
  useResettableState,
  shouldOpenInNewTab,
} from 'utils';
import { paginationSizePerPage as limit } from 'utils/constants';
import { isContractorAdminPortal } from 'utils/contractorMemberUser';
import { getJobsiteIdsForDevelopers } from 'utils/filters';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { SortOrder } from 'utils/useQueryOrderBy';
import { getColumns, getFilterItems, orderByFields } from './helpers/tables';
import {
  JobsiteWorkerOnboardingFilterValue,
  jobsiteWorkerOnboardingStatusMapping,
} from './helpers/utils';
import { OnboardingFilters, OnboardingJobsiteWorkerAccess } from './types';

export function WorkerOnboardingsContainer(): ReactElement {
  const history = useHistory();
  const { currentUser: user } = React.useContext(AuthContext);
  const isNotContractorUser = !user.isContractor;

  const defaultStartDate = moment(getCurrentISOFormattedDate());
  const defaultEndDate = moment(getCurrentISOFormattedDate());

  const {
    page,
    orderBy: defaultSortField,
    orderByDesc: isDescending,
    startDate,
    endDate,
    developerIds,
    jobsiteIds,
    contractorIds,
    onboardingStatuses,
    updateUrl,
    loading: isUrlLoading,
  } = usePageQueryParams({
    updateJobsiteSelection: true,
    defaultValues: {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    },
  });

  const offset = page * limit;
  const {
    value: isAddWorkerModalOpen,
    setTrue: openAddWorkerModal,
    setFalse: closeAddWorkerModal,
  } = useBoolean(false);
  const {
    value: { then: onEditSelfOnboardingWorkerConfirm },
    setValue: confirmSelfOnboardingWorkerEdit,
    resetValue: closeEditSelfOnboardingWorkerWarningModal,
  } = useResettableState<{ then: () => void }>({ then: null }, { then: null });

  const goToOnboardingPersonalInformation = React.useCallback(
    (jobsiteWorkerId: string, event: AnyValue): void => {
      const path = `/onboarding/${jobsiteWorkerId}/personal-information`;
      if (shouldOpenInNewTab(event)) {
        window.open(path, '_blank');
      } else {
        history.push(path, {
          workerOnboardingsQueryParams: history.location.search,
        });
      }
    },
    [history],
  );

  const goToWorkerProfile = React.useCallback(
    (workerId: string, event: AnyValue): void => {
      const path = `/worker/${workerId}`;
      if (shouldOpenInNewTab(event)) {
        window.open(path, '_blank');
      } else {
        history.push(path);
      }
    },
    [history],
  );

  const { tableSortInfo, orderBy, setNewOrderBy } = useQueryOrderBy<
    string,
    GetJobsiteWorkersOrderByField
  >(
    defaultSortField,
    isDescending,
    (tableField: string): GetJobsiteWorkersOrderByField =>
      orderByFields[tableField],
  );

  const tableSortBy: Array<SortingRule<string>> = tableSortInfo?.dataField
    ? [{ id: tableSortInfo.dataField, desc: tableSortInfo.order === 'desc' }]
    : [];

  const orientationStatuses = onboardingStatuses
    ?.filter((status): status is JobsiteWorkerOnboardingFilterValue =>
      isStringEnumValue(JobsiteWorkerOnboardingFilterValue, status),
    )
    .map((status) => jobsiteWorkerOnboardingStatusMapping[status])
    .filter(Boolean);

  const {
    jobsites,
    loading: userJobsitesLoading,
    error: userJobsitesError,
  } = useAvailableJobsites();

  const {
    data: jobsiteWorkerData,
    error: jobsiteWorkerError,
    networkStatus: jobsiteWorkerNetworkStatus,
    refetch: refetchOnboardingJobsiteWorkers,
  } = useGetOnboardingJobsiteWorkersQuery({
    fetchPolicy: 'no-cache',
    pollInterval: 60000,
    notifyOnNetworkStatusChange: true,
    skip: isUrlLoading,
    variables: {
      jobsiteIds,
      contractorIds,
      orientationStartDate: startDate?.toDate(),
      orientationEndDate: endDate?.toDate(),
      limit,
      offset,
      // timezoneName: moment.tz.guess(),
      orientationStatuses,
      orderBy,
    },
  });

  const { user: userData } = jobsiteWorkerData?.getCurrentSession ?? {};
  const contractors = userData?.contractors.edges.map(({ node }) => node);

  const jobsiteWorkerAccesses = jobsiteWorkerData?.getJobsiteWorkers?.edges;
  const jobsiteWorkerAccessesCount =
    jobsiteWorkerData?.getJobsiteWorkers?.totalCount ?? 0;
  const jobsiteStats = jobsiteWorkerData?.getJobsiteWorkers?.jobsiteStats;

  const pageCount = Math.ceil(jobsiteWorkerAccessesCount / limit);

  const columns = React.useMemo(() => getColumns(), []);
  const filterItems = React.useMemo(
    () =>
      getFilterItems({
        jobsites,
        jobsiteStats,
        contractors,
        developerIds,
        jobsiteIds,
        contractorIds,
        startDate,
        endDate,
        onboardingStatuses,
      }),
    [
      jobsites,
      jobsiteStats,
      developerIds,
      jobsiteIds,
      contractorIds,
      startDate,
      endDate,
      onboardingStatuses,
    ],
  );

  const onSortByChangeHandler = (
    sortBy: Array<SortingRule<OnboardingJobsiteWorkerAccess>>,
  ): void => {
    const [firstSortBy] = sortBy ?? [];
    const sortField = firstSortBy?.id ?? null;
    const sortOrder: SortOrder = firstSortBy?.desc ? 'desc' : 'asc';
    const newOrderBy = sortField && { field: sortField, order: sortOrder };
    setNewOrderBy(newOrderBy);
    updateUrl({
      orderBy: sortField,
      orderByDesc: sortField && sortOrder === 'desc' ? true : null,
    });
  };

  const onPageChangeHandler = (_pageSize: number, pageIndex: number): void => {
    updateUrl({ page: pageIndex ? pageIndex + 1 : null });
  };

  const onFilterChangeHandler = (
    changedFilters: Partial<OnboardingFilters>,
  ): void => {
    const { dateRange: dateRangeFilter, ...restChangedFilters } =
      changedFilters;
    const dateRange =
      dateRangeFilter === null
        ? { startDate: null, endDate: null }
        : dateRangeFilter;
    updateUrl({
      page: null,
      ...dateRange,
      ...restChangedFilters,
      ...getJobsiteIdsForDevelopers(jobsites, changedFilters.developerIds),
    });
  };

  const onRowClickHandler: TableProps<OnboardingJobsiteWorkerAccess>['onRowClick'] =
    ({ data: jobsiteWorkerAccess, event }): void => {
      const { jobsiteWorker } = jobsiteWorkerAccess ?? {};
      const { jobsiteWorkerId } = jobsiteWorker ?? {};
      const workerId = jobsiteWorker?.contractorWorker?.worker?.workerId;
      if (isContractorAdminPortal(user) && workerId) {
        goToWorkerProfile(workerId, event);
        return;
      }
      if (
        jobsiteWorker.orientationStatus ===
        JobsiteWorkerOrientationStatus.SelfOnboarding
      ) {
        confirmSelfOnboardingWorkerEdit({
          then: () => goToOnboardingPersonalInformation(jobsiteWorkerId, event),
        });
      } else {
        goToOnboardingPersonalInformation(jobsiteWorkerId, event);
      }
    };

  const menuItems = ensureNonEmptyItems<MenuItemProps>([
    isNotContractorUser &&
      user.isAllowed(to.addWorkers) && {
        text: 'Add worker',
        icon: UserPlusIcon,
        onClick: openAddWorkerModal,
      },
  ]);

  const loading: boolean =
    isQueryLoading(jobsiteWorkerNetworkStatus) || userJobsitesLoading;
  const error: ApolloError = jobsiteWorkerError || userJobsitesError;

  if (error) {
    return <LoadingError error={error} />;
  }

  return (
    <Container className="worker-onboardings-container">
      <NewHeader
        title="Jobsite Onboarding"
        titleInfo={jobsiteWorkerAccessesCount}
        actionsProps={{
          menuItems,
          onReloadPressed: refetchOnboardingJobsiteWorkers,
        }}
      />
      <TableContainer>
        <Filter
          items={filterItems}
          loading={loading}
          onChange={onFilterChangeHandler}
        />
        <Table
          loading={loading}
          columns={columns}
          data={jobsiteWorkerAccesses}
          initialState={{ sortBy: tableSortBy, pageSize: limit }}
          pageCount={pageCount}
          pageIndex={page}
          remote
          enablePagination
          onRowClick={onRowClickHandler}
          onPageChange={onPageChangeHandler}
          onSortByChange={onSortByChangeHandler}
          disableGlobalFilter
        />
      </TableContainer>
      <AddWorkerModal
        isOpen={isAddWorkerModalOpen}
        closeModal={closeAddWorkerModal}
        defaultJobsiteId={jobsiteIds?.[0]}
      />
      <EditSelfOnboardingWorkerWarningModal
        isOpen={!!onEditSelfOnboardingWorkerConfirm}
        closeModal={closeEditSelfOnboardingWorkerWarningModal}
        onConfirm={(): void => {
          onEditSelfOnboardingWorkerConfirm();
          closeEditSelfOnboardingWorkerWarningModal();
        }}
      />
    </Container>
  );
}
